import { EEntityType } from '@/editor/types';
import { OperateAbstract } from './OperateAbstract';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { moveAssembly } from '@/editor/controller/utils';
import { VirtualAssemblyEntities } from '@/editor/entities/virtualModelEntities';
import AssemblyEntity from '@/editor/entities/assemblyEntity';
import { globalStores } from '@/editor/stores';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { IAssemblyData } from '@/editor/data/assembly/assembly';
import { cloneDeep } from 'lodash-es';
import { AssemblyAbsorbManager } from '@/editor/manager/absorb/box/assemblyAbsorbManager';
import { OperateSelectedBoxes } from '@/editor/controller/helper/operateSelectedBoxes';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';

export class OperateAssembly extends OperateAbstract<EEntityType.Assembly> {
  private _checkedEntity: AssemblyEntity | null | undefined = null;
  /** 移动前的原始数据，虚拟实体要拿它作为子家具位置映射的源，所以整个移动过程中保持不变 */
  private _virtualAssemblyData: IAssemblyData | null | undefined = null;
  /** 移动后的数据，没移动过时为 null */
  private _movedAssemblyData: IAssemblyData | null = null;
  private _virtualEntities = new VirtualAssemblyEntities();

  private _virtualRulerModel = new RulerModel();
  private _assemblyAbsorbManager = new AssemblyAbsorbManager();
  private _selectedBoxes = new OperateSelectedBoxes();

  private _extraOulines: { start: IPoint2d; end: IPoint2d }[] = [];

  set checkedEntity(entity: AssemblyEntity | null | undefined) {
    if (entity !== this._checkedEntity && this._checkedEntity) {
      this._checkedEntity.selected = false;
    }

    this._checkedEntity = entity;

    if (this._checkedEntity) {
      this._checkedEntity.selected = true;
      this._assemblyAbsorbManager.updateData({ excludeAssemblyIds: [this._checkedEntity.id] });
      this._extraOulines = getBoxRayDetectOutlines(
        globalStores.getDataByIdType(this._checkedEntity.id, this._checkedEntity.type)?.accessorieIds
      );
    }
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  private updateEntityInfos(moveAssemblyData: IAssemblyData) {
    if (!this._virtualAssemblyData) return;

    this._virtualEntities.update(this._virtualAssemblyData, moveAssemblyData);
  }

  private getAssemblyBoxData(point: IPoint2d, assemblyData: IAssemblyData) {
    const boxData = {
      center: point,
      width: assemblyData.width,
      thickness: assemblyData.thickness,
      rotation: assemblyData.rotation
    };
    const absorbResult = this._assemblyAbsorbManager.calculateThenRender(boxData);
    if (absorbResult) {
      boxData.center = absorbResult.absorbPoint;
      boxData.rotation = absorbResult.rotation;
    }

    return boxData;
  }

  override startMove(_point: IPoint2d): void {
    this.moving = true;
    if (!this._checkedEntity) return;

    const assemblyData = globalStores.assemblyStore.getAssemblyData(this._checkedEntity.id);
    if (!assemblyData) return;

    this._checkedEntity.setVisible(false);
    this._virtualAssemblyData = cloneDeep(assemblyData);
    this.updateEntityInfos(this._virtualAssemblyData);

    const rulerInfos = calculateBoxRulerInfos(assemblyData, this._extraOulines);
    this._virtualRulerModel.updateModel(rulerInfos);

    this.updateBoxes();
  }

  move(point: IPoint2d) {
    this.moving = true;

    if (!this._checkedEntity || !this._virtualAssemblyData) {
      return point;
    }

    const boxData = this.getAssemblyBoxData(point, this._virtualAssemblyData);
    this.updateMovedAssembly(boxData.center, boxData.rotation);
  }

  override moveByOffset(offset: IPoint2d) {
    if (!this._checkedEntity || !this._virtualAssemblyData) return;

    this.moving = true;

    // 累积在移动后的数据上，_virtualAssemblyData 要保持原始位置
    const base = this._movedAssemblyData ?? this._virtualAssemblyData;
    this.updateMovedAssembly(Point2dUtils.added(base.center, offset), base.rotation);
  }

  /** 按目标位置更新虚拟实体、标尺和选中框 */
  private updateMovedAssembly(center: IPoint2d, rotation: number) {
    if (!this._virtualAssemblyData) return;

    this._movedAssemblyData = moveAssembly(center, { ...this._virtualAssemblyData, rotation });
    this.updateEntityInfos(this._movedAssemblyData);

    const rulerInfos = calculateBoxRulerInfos(this._movedAssemblyData, this._extraOulines);
    this._virtualRulerModel.updateModel(rulerInfos);

    this.updateBoxes({ x: center.x, y: center.y, rotation });
  }

  completeMove(_point: IPoint2d) {
    this.moving = false;

    if (!this._checkedEntity) return;

    // 没有移动过（如只是点了一下）就不写数据，但真实实体的可见性一定要恢复
    if (this._movedAssemblyData) {
      globalStores.assemblyOperation.moveAssembly(
        this._checkedEntity.id,
        this._movedAssemblyData.center,
        this._movedAssemblyData.rotation
      );
    }

    this._checkedEntity.setVisible(true);

    this._virtualRulerModel.clear();
    this._assemblyAbsorbManager.clearModel();
    this._virtualEntities.dispose();
  }

  private updateBoxes(params?: { x: number; y: number; rotation: number }) {
    if (!this._checkedEntity?.id) return;

    this._selectedBoxes.update({ type: EEntityType.Assembly, entityId: this._checkedEntity.id }, params);
  }

  clear() {
    // 兜底：startMove 隐藏过真实实体，任何退出路径都要恢复
    this._checkedEntity?.setVisible(true);

    this.checkedEntity = null;
    this.moving = false;
    this.pickPoint = null;
    this._virtualAssemblyData = null;
    this._movedAssemblyData = null;
    this._virtualRulerModel.clear();
    this._virtualEntities.dispose();
    this._assemblyAbsorbManager.clearModel();
    this._selectedBoxes.clear();
  }

  dispose() {
    this.clear();

    this._virtualRulerModel.dispose();
    this._assemblyAbsorbManager.dispose();
    this._selectedBoxes.dispose();
  }
}
